import { Component, Input, ViewChild, Output, EventEmitter, ElementRef } from '@angular/core';
import { RecipientProvider } from '../../providers/recipient/recipient';
import { IRecipientBank } from '../../model/models/recipient/recipient-bank.interface'
import { FormControlProvider } from '../../providers/formbuilder/formcontrol';
import { PriceCalculatorProvider } from '../../providers/price-calculator/price-calculator';
import { IFormControl } from '../../providers/formbuilder/formcontrol.interface';
import { NavParams } from 'ionic-angular';
import { ErrorCodeType, ErrorKeyType } from '../../model/types/error-code-type';
import { LoggerProvider, LoggerEvent } from '../../providers/logger/logger';
import { RouteProvider } from '../../providers/route/route';
import { TranslateService } from '@ngx-translate/core';
import { IRecipient } from '../../model/models/recipient/recipient.interface';
import { HelpProvider } from '../../providers/help/help';
import { IOrder } from '../../model/models/order/order-interface';
import { RmtPopupComponent } from './../../components/rmt-popup/rmt-popup';
import { RecipientMapper } from '../../providers/recipient/recipient.mapper';
import { VirtualPaymentType, BankAccountFormatId } from '../../model/types/vpa-type';
import { OrderProvider } from '../../providers/order/order';
import { ContactsProvider } from '../../providers/contacts/contacts';
import { DeliveryMethodType } from '../../model/types/delivery-method-type';
import { ResourceProvider } from '../../providers/resource/resource';
import { FormFieldMergeTransformer } from '../../providers/formbuilder/form-field-merge-transformer';
import { IFormFieldMergeConfig } from '../../providers/formbuilder/form-field-merge-config.interface';
import { FORM_FIELD_MERGE_CONFIGS } from '../../providers/formbuilder/form-field-merge-configs';
import { FormFieldOverrideTransformer } from '../../providers/formbuilder/form-field-override-transformer';
import { FORM_FIELD_OVERRIDE_CONFIGS } from '../../providers/formbuilder/form-field-override-configs';
import { getCurrencyDisplayName } from '../../pipes/currency-pipe';
import { BankDepositSmartRoutingProvider } from '../../providers/bank-deposit-smart-routing/bank-deposit-smart-routing';
import { IBankDepositIsSmartRoutingEnabledRequestEntity } from '../../model/api/bank-deposit';
import { IBankDepositSmartRoutingProvider } from '../../model/models/agent/bank-deposit-provider.interface';
import { FormControlMapper } from '../../providers/formbuilder/formcontrol-mapper';
import { APP_CONFIG } from '../../environment';

/** Recipient fields whose value only means something under the account format it was saved with. */
const FORMAT_SPECIFIC_FIELDS = ['bankAccountNumber', 'bankAccountNumberUnitary', 'bankRoutingCode'];

/** Formats that hold the same account number, differing only in which field stores it. */
const INTERCHANGEABLE_ACCOUNT_FORMATS = [BankAccountFormatId.BankAccount, BankAccountFormatId.BankAccountUnitary].map(String);
@Component({
  selector: 'rmt-bank-deposit',
  templateUrl: 'rmt-bank-deposit.html'
})
export class RmtBankDepositComponent {
  @Input() recipient :IRecipient;
  @Input() country: string;
  @Input() currencyCode: string;
  @Input() amountFrom: number;
  @Input() isWalletBankTransfer: boolean;
  @Input() sanitizeFields: [string];
  @Input() disabled: boolean;
  @Input() clearOnFocus: boolean;
  @Input() isShowBankNamePlaceHolder: boolean;
  @Output() onSelectedBank = new EventEmitter<number>();
  @Output() onFieldsAutofill = new EventEmitter<IFormControl[]>();
  @ViewChild('form') form;
  @ViewChild('bank') bank;
  @ViewChild('bank', { read: ElementRef }) bankRef;
  @ViewChild('format') format;
  banks: IRecipientBank[];
  selectedBankId: number;
  selectedBankName;
  selectedBankNameArray: [{}];
  selectedBank: IRecipientBank;
  searchingBank: boolean = false;
  bankLocationNumberFieldExists: boolean = false;
  bankRoutingCodeFieldExists: boolean = false;
  bankLocationNameFieldExists: boolean = false
  calculatePromise;
  hasBank: boolean = false;
  inputTimeout;
  lastSearch;
  fields: IFormControl[];
  bankId;
  branchLookupExceptions: any = ['BR', 'GY', 'JM', 'JP'];
  // HT (Haiti) intentionally excluded: bank deposit only reliably supports Unibank, so
  // customers go straight to the full bank search instead of the curated Top Banks screen.
  topCountriesList = ['IN', 'MX','PH','CO','PK','BD','EC','SV','MA','DO','PE','US','ZA','UA', 'LK','PY'];
  allowBranchLookup = true;
  preSelectedBank;
  order: IOrder;
  actionsheetNavInfo = this.routeProvider.pages.ActionsheetSelectPage;
  bankAccountFormatTitle = '';
  bankAccountFormats = [];
  bankAccountFormatId = null;
  loadingFields = false;
  bankNameFieldHidden = false;
  rmtEmptyPickerHeading: string = this.translate.instant('page.sendSummaryPage.detailsBankDeposit.bankName');
  rmtEmptyPickerPlaceholder: string = this.translate.instant('page.sendMoneyRecipientBankDepositPage.bankNameText');
  showPagoMovilFormatField: boolean = false;
  hideAccountFormatField: boolean = false;
  activeMergeConfig: IFormFieldMergeConfig = null;
  /** Set by the last requirements response that reported the bank cannot receive this transfer. */
  unsupportedAccountFormat = false;
  private smartRoutingCorridor: IBankDepositIsSmartRoutingEnabledRequestEntity = null;
  private openingBankSearch = false;
  private preserveAccountDetails = false;
  private isModeUpdateBankDepositDetails = false;

  constructor(private routeProvider: RouteProvider,
              private navParams: NavParams,
              private recipientProvider: RecipientProvider,
              private calcProvider: PriceCalculatorProvider,
              private formProvider: FormControlProvider,
              private helpProvider: HelpProvider,
              private orderProvider: OrderProvider,
              private resourceProvider: ResourceProvider,
              private bankDepositSmartRoutingProvider: BankDepositSmartRoutingProvider,
              private contactsProvider: ContactsProvider,
              private translate: TranslateService) {
    this.recipient = this.navParams.get('recipient');

    if (this.navParams.get('country')) {
      this.country = this.navParams.get('country');
    }
    if(this.navParams.get('bankNameFieldHidden')) {
      this.bankNameFieldHidden = this.navParams.get('bankNameFieldHidden');
    }
    this.order = this.navParams.get('order') || {};
    this.order.isPagoMovil = false;
    this.preserveAccountDetails = !!this.navParams.get('preserveAccountDetails');
    this.isModeUpdateBankDepositDetails = !!this.navParams.get('isModeUpdateBankDepositDetails');
  }

  ignoresSavedBank(): boolean {
    return !!this.recipient
      && !this.isModeUpdateBankDepositDetails
      && this.order.deliveryMethodInternal === DeliveryMethodType.PagoMovil
      && !RecipientMapper.hasPagoMovilDetails(this.recipient);
  }

  ngAfterViewInit() {
    if (this.recipient && !RecipientMapper.isDirectDebitPayoutAgent(this.recipient)) {
      if (this.recipient.bankName && !this.ignoresSavedBank()) {
        this.bank.value = this.recipient.bankName;
        if (this.selectedBank) {
          this.selectedBankName = this.selectedBank.displayName || this.selectedBank.matchValue || this.selectedBank.bankName;
        } else if (!RecipientMapper.isDirectDebitPayoutAgent(this.recipient)) {
          this.selectedBankName = this.recipient.bankName;
        }
      }
      if (this.recipient.bankAccountFormatId) {
        this.bankAccountFormatId = this.recipient.bankAccountFormatId.toString();
        if (this.bankAccountFormatId == BankAccountFormatId.PagoMovil.toString() && this.country !== "VE" && this.order.deliveryMethodInternal == DeliveryMethodType.BankDeposit) {
          this.bankAccountFormatId = "111806";
        }
        // TODO !!!!!!!!!!!!!!!!!!!!!!!!!!! TEMPORARY WORKAROUND since we get the wrong value from the backend
        if (this.bankAccountFormatId == "111805" && this.recipient.bankAccountNumber && !this.recipient.bankAccountNumberUnitary)
          this.bankAccountFormatId = "111806";
      }
      if (RecipientMapper.hasPagoMovilDetails(this.recipient)) {
        this.bankAccountFormatId = BankAccountFormatId.PagoMovil.toString();
        this.order.isPagoMovil = true;
      }
    }

    if (this.order.deliveryMethodInternal === DeliveryMethodType.PagoMovil)
      this.bankAccountFormatId = BankAccountFormatId.PagoMovil.toString();

    if(this.isWalletBankTransfer ===undefined || this.isWalletBankTransfer ===null)
      this.isWalletBankTransfer=false;
    if (this.isWalletBankTransfer)
      this.loadingFields = false; // no fields will be loaded for wallet bank transfer
    if ((!this.currencyCode || !this.amountFrom) && !this.isWalletBankTransfer) { // add iswalletbankTransfer to prevent calculate api request 
      this.getDefaults();
    }

    if (this.branchLookupExceptions.includes(this.country))
      this.allowBranchLookup = false;
    this.selectedBankNameArray = [{ text: this.selectedBankName, value: this.selectedBankName, isSelected: true }]

    if(this.isShowBankNamePlaceHolder){
      this.rmtEmptyPickerHeading = this.translate.instant("page.bankValidation.bankNameField.label")
      this.rmtEmptyPickerPlaceholder = this.translate.instant("page.bankValidation.bankNameField.label")
    }
  }

  focus() {
    this.bankRef.nativeElement.getElementsByTagName('input')[0].focus();
  }

  getForm() {
    if (this.form)
      return this.form.form;
    return null;
  }

  async getDefaults() {
    const calcPayload = {
      countryTo: this.country
    };

    try {
      this.calculatePromise = this.calcProvider.calculateOrderTotals(this.resourceProvider, calcPayload);
      await this.calculatePromise;
    }
    catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.ComponentException, 'Failed to calculate defaults for bank deposit component');
    }
  }

  deselect() {
    this.onSelectedBank.emit(null);
    this.hasBank = false;
    this.selectedBank = null;
    this.fields = null;
    this.banks = null;
    this.lastSearch = null;
    this.bankAccountFormats = [];
    this.showPagoMovilFormatField = false;
    this.hideAccountFormatField = false;
    this.activeMergeConfig = null;
    this.unsupportedAccountFormat = false;
  }

  onFieldChange(event) {
    if (!event || !this.activeMergeConfig || !this.form || !this.form.form) return;
    FormFieldMergeTransformer.handleFieldChange(
      event.text, event.value, this.form.form, this.activeMergeConfig
    );
  }

  changeBank() {
    if (this.inputTimeout)
      window.clearTimeout(this.inputTimeout);

    this.lastSearch = null;
    this.inputTimeout = window.setTimeout(() => {
      this.inputTimeout = null;

      this.searchBank();
    }, 250);
  }

  async searchBank() {
    this.deselect();
    if ((this.bank.value.trim().length < 3) ||
        (this.selectedBank && this.selectedBank.bankName === this.bank.value))
      return;

    this.searchingBank = true;
    this.bankLocationNumberFieldExists = false;
    this.bankRoutingCodeFieldExists = false;
    this.bankLocationNameFieldExists = false;
    const search = this.searchKey();
    this.lastSearch = search;
    await this.ensureSmartRoutingCorridor();
    const payload = this.getPayload();

    let banks = null;
    try {
      const bankObj = await this.recipientProvider.searchRecipientBanks(payload);
      banks = bankObj ? bankObj.banks: [];
    }
    catch (err) {
      if (err.status === 404)
        LoggerProvider.warning(this, LoggerEvent.ComponentException, 'Bank not found in bank deposit component', err);
      else
        LoggerProvider.exception(this, err, LoggerEvent.ComponentException, 'Failed to search recipient banks for bank deposit component');
    }

    let totals = null;
    try {
      // if no currencyCode or amount to has been supplied determine the defaults for the country
      if (this.calculatePromise)
        totals = await this.calculatePromise;
    }
    catch (err) {
      if (err.errorCode === ErrorCodeType.AmountFromNotFound && err.result) {
        totals = err.result;
        LoggerProvider.info(this, 'Amount from not found in bank deposit component');
      }
      else
        LoggerProvider.exception(this, err, LoggerEvent.ComponentException, 'Failed to wait for calculate promise in bank deposit component');
    }
    if (totals) {
      if (!this.currencyCode && totals.currencies && totals.currencies.length > 0)
        this.currencyCode = totals.currencies[0].currencyCode;
      if (!this.amountFrom)
        this.amountFrom = totals.minimumAmountFrom;
    }

    if (!banks) // if no result has been returned wait a bit because perhaps he is still entering
      await new Promise((resolve) => { window.setTimeout(() => { resolve(true); }, 250); });

    if (this.lastSearch === search) {
      this.banks = banks;
      if (this.banks && this.banks.length === 1) {
        let routingCode = null;
        if (payload.bankCode && this.banks[0].bankName.toLowerCase().indexOf(payload.bankCode.toLowerCase()) === -1) {
          if (payload.bankCode.length <= 4) // special case for strange results i.e. enter 012 for Mexico and you get back BANCOMER
            this.banks = null;
          else
            routingCode = payload.bankCode;
        }
        if (this.banks)
          this.selectBank(this.banks[0], routingCode);
      } else if (this.banks && this.selectedBankId !== null) {
        const newBanks = this.banks.filter((e) => e.bankId === this.selectedBankId);

        if (newBanks.length > 0) {
          let routingCode = null;
          routingCode = payload.bankCode;
          this.selectBank(newBanks[0], routingCode);
        }
      }
      this.searchingBank = false;
    }
  }

  private searchKey(): string {
    return JSON.stringify({ countryTo: this.country, bankCode: this.bank.value });
  }

  getPayload() {
    const payload: any = {
      countryTo: this.country,
      currencyFrom: this.smartRoutingCorridor ? this.smartRoutingCorridor.currencyFrom : undefined,
      currencyTo: this.smartRoutingCorridor ? this.smartRoutingCorridor.currencyTo : undefined,
      amountFrom: this.smartRoutingCorridor ? this.smartRoutingCorridor.amountFrom : undefined,
      amountTo: this.smartRoutingCorridor ? this.smartRoutingCorridor.amountTo : undefined,
      stateFrom: this.smartRoutingCorridor ? this.smartRoutingCorridor.stateFrom : undefined,
      stateTo: this.smartRoutingCorridor ? this.smartRoutingCorridor.stateTo : undefined,
      bankName: this.bank.value,
      bankCode: this.bank.value,
      page: 0
    };
    return payload;
  }

  selectBank(bank, routingCode = null) {
    this.bankLocationNumberFieldExists = false;
    this.bankRoutingCodeFieldExists = false;
    this.bankLocationNameFieldExists = false;
    this.deselect();
    this.selectedBank = bank;
    if (bank) {
      const name = bank.displayName || bank.matchValue || bank.bankName;
      this.bank.value = name;
      window.clearTimeout(this.inputTimeout);

      if(this.isWalletBankTransfer ===true){ 
        this.hasBank = false; // to prevent loading of of form fields
        this.bankId = bank.bankId;
        this.selectedBankId = bank.bankId;
        if (!!bank.displayName) {
          this.selectedBankName = bank.displayName;
        } else if (!!bank.matchValue) {
          this.selectedBankName = bank.matchValue;
        } else if (!!bank.bankAlias) {
          this.selectedBankName = bank.bankAlias;
        } else {
          this.selectedBankName = bank.bankName;
        }
        this.onSelectedBank.emit(bank.bankId);
        return;
      }
      this.onSelectedBank.emit(null);
      this.hasBank = true;
      // Reset stale bankAccountFormatId when switching to a different bank
      // so the new bank's default format is used instead of the previous bank's format.
      // The previous bank is the saved recipient's when there is one, otherwise the last bank we
      // loaded fields for: with no saved recipient the format is latched from that bank's dropdown
      // just the same, and carrying it over asks the API for a format the new bank never offered.
      // PagoMovil keeps its own format across banks, as ngAfterViewInit set it, instead of taking the dropdown's first entry.
      const previousBankId = this.recipient ? this.recipient.bankId : this.selectedBankId;
      if (previousBankId !== null && previousBankId !== undefined && bank.bankId !== previousBankId) {
        this.bankAccountFormatId = this.order.deliveryMethodInternal === DeliveryMethodType.PagoMovil
          ? BankAccountFormatId.PagoMovil.toString()
          : null;
      }
      this.bankId = bank.bankId;

      if (!!bank.displayName) {
        this.selectedBankName = bank.displayName;
      } else if (!!bank.matchValue) {
        this.selectedBankName = bank.matchValue;
      } else {
        this.selectedBankName = bank.bankName;
      }

      this.getBankFields(routingCode);
    }
  }

  /** formatRetried: the PagoMovil-fields retry below already ran for this selection; every re-entry passes it on so it runs at most once. */
  async getBankFields(routingCode = null, formatRetried = false) {
    await this.ensureSmartRoutingCorridor();
    this.loadingFields = true;
    this.unsupportedAccountFormat = false;
    const params = {
      bankId: this.bankId,
      amountFrom: this.amountFrom,
      deliveryMethodID: this.order.deliveryMethodInternal === DeliveryMethodType.PagoMovil
        ? VirtualPaymentType.PagoMovil
        : 2, // Not exactly sure why the API is asking for this on a call specific to recipient bank deposit and only takes an int.
      countryTo: this.country,
      currencyTo: this.currencyCode,
      //recipientId: currentOrder.recipient.id,
    }
  
    const requestedFormatId = this.bankAccountFormatId;
    if (requestedFormatId)
      params['bankAccountFormatId'] = requestedFormatId;

    try {
      var fields: IFormControl[] = null;
      if (this.bankDepositSmartRoutingProvider.useBankDepositSmartRouting) {
        fields = await this.getSmartRoutingBankFields(params);
        // No account format can receive this transfer, so there is no legacy form to fall back to
        // either. Covers both entry points, since the format picker re-enters through here.
        if (this.unsupportedAccountFormat) {
          this.clearBankFieldsForUnsupportedFormat();
          this.openUnsupportedAccountFormatAlert();
          return;
        }
      }
      if (!fields)
        fields = await this.formProvider.getFields({ uri: 'recipient/bank-deposit', params}, false, null, (res) => {
          const dropdown = res && res.bankAccountFormatDropdown;
          this.applyBankAccountFormats(
            (dropdown && dropdown.options) ? dropdown.options.map((option) => ({ value: option.value.toString(), text: option.name })) : null,
            dropdown ? dropdown.label : null);
        });

      // If the format was corrected (stale format not valid for this bank), re-fetch with the correct one
      if (requestedFormatId && requestedFormatId != this.bankAccountFormatId) {
        await this.getBankFields(routingCode, formatRetried);
        return;
      }

      // A bank whose default format is PagoMovil answers a bank-deposit request with the PagoMovil
      // form, even while the same response offers an account-number format in its dropdown. Nothing
      // was requested on a first selection, so the block above cannot catch it. Ask again for the
      // format that dropdown resolved to, so a bank deposit gets bank fields. Bounded to one retry by
      // formatRetried, which the BankDepositProviderNotFound fallback in the catch also carries.
      const isFirstRequest = !requestedFormatId && !formatRetried;
      const resolvedToAccountFormat = !!this.bankAccountFormatId && this.bankAccountFormatId !== BankAccountFormatId.PagoMovil.toString();
      const isBankDepositDelivery = this.order.deliveryMethodInternal !== DeliveryMethodType.PagoMovil;
      const returnedPagoMovilFields = !!fields && fields.some((field) => field.id === 'virtualPaymentAccountNo');
      if (isFirstRequest && resolvedToAccountFormat && isBankDepositDelivery && returnedPagoMovilFields) {
        await this.getBankFields(routingCode, true);
        return;
      }
      if (fields) {
        if (this.currencyCode === 'CAD') {
          let routingCodeIdx = fields.map(f => f.id).indexOf('bankRoutingCode');
          if (routingCodeIdx !== -1) {
            fields[routingCodeIdx].hint = this.translate.instant('form.recipientBankDepositForm.bankRoutingNoCA.label');
          }
        }

        // Morocco: Override "Account Number" label to "RIB" for clarity
        if (this.country === 'MA') {
          const accountField = fields.find(f => f.id === 'bankAccountNumberUnitary' || f.id === 'bankAccountNumber');
          if (accountField) {
            accountField.label = 'RIB';
          }
        }

        this.fields = fields.filter(f => f.id !== 'bankId');
        if (routingCode) {
          const routing = fields.find(f => f.id === 'bankRoutingCode');
          if (routing) {
            routing.value = routingCode;
          }
        }
        this.onSelectedBank.emit(this.bankId);
        this.selectedBankId = this.bankId;
      }
      else {
        this.hasBank = false;
        this.onSelectedBank.emit(null);
      }
      if (fields && fields.length > 0) {
        this.checkIfBankLocationNumberExist(fields);
        this.checkIfBankRoutingCodeExist(fields);
        this.checkIfBankLocationNameFieldExist(fields);
      }
      if (fields && this.recipient && this.recipient.bankId === this.bankId) {
        const sameFormat = this.recipient.bankAccountFormatId == this.bankAccountFormatId
          || this.bankAccountFormatId == BankAccountFormatId.PagoMovil;
        if (sameFormat || this.preserveAccountDetails)
          this.prefillRecipientFields(fields, sameFormat);
      }
      else {
        this.recipient = null;
      }

      this.activeMergeConfig = FormFieldMergeTransformer.findMatchingConfig(this.fields, FORM_FIELD_MERGE_CONFIGS);
      if (this.activeMergeConfig) {
        this.fields = FormFieldMergeTransformer.mergeFields(this.fields, this.activeMergeConfig, this.translate);
      }

      this.fields = FormFieldOverrideTransformer.applyOverrides(
        this.fields,
        { country: this.country, deliveryMethod: this.order.deliveryMethodInternal || undefined },
        FORM_FIELD_OVERRIDE_CONFIGS,
      );

      this.loadingFields = false;
      this.onFieldsAutofill.emit(this.fields);
    }
    catch(error) {
      this.hasBank = false;
      const errorCode: string | undefined = error && error.errorCurrent && error.errorCurrent.errorCode;
      const errorMessage: string | undefined = error &&
        error.errorCurrent &&
        error.errorCurrent.message &&
        error.errorCurrent.message.toLowerCase();

      if (errorCode === 'BankDepositProviderNotFound') {
        if (this.bankAccountFormatId) {
          // when a bank account format has been given try it without since the bank eventually is not configured properly
          this.bankAccountFormatId = null;
          this.hasBank = true;
          await this.getBankFields(routingCode, formatRetried);
          return;
        }

        const minimumValueError = 'Transfer Amount Is Less Than Minimum Value Acceptable';
        if (errorMessage.includes(minimumValueError.toLowerCase())) {
          this.openMinMaxAlert(errorMessage, true);
          return;
        }

        const maximumValueError = 'Transfer Amount Is Greater Than Maximum Value Acceptable';
        if (errorMessage.includes(maximumValueError.toLowerCase())) {
          this.openMinMaxAlert(errorMessage, false);
          return;
        }
        else
          this.openCannotRetreiveBankFieldsAlert();
      }
      if (errorCode === ErrorCodeType.BankDepositProviderCurrencyNotSupported) {
        this.openUnavailableCurrencyAlert();
        return;
      }
    }
  }

  openUnavailableCurrencyAlert() {
    this.routeProvider.openActionSheet(
      this.routeProvider.pages.ModalAlertPage,
      {
        image: 'assets/imgs/yellow-warning_bg_color.svg',
        title: this.translate.instant('page.recipientBankDetailsPage.currencyNotSupportedHeader'),
        subtitle: this.translate.instant(
          'page.recipientBankDetailsPage.currencyNotSupportedContent',
          { currency: getCurrencyDisplayName(this.currencyCode) }),
        primaryButtonText: this.translate.instant('page.recipientBankDetailsPage.updateCurrencyButton'),
        showPrimaryButton: true,
        primaryAction: () => this.redirectToCheckPrices(),
        showSecondaryButton: true,
        secondaryButtonText: this.translate.instant('page.recipientBankPage.changeBankButton'),
        secondaryAction: () => this.openBankSearchScreen(),
        showFullPage: false,
        isModal: true,
        hideHeader: true,
      }, null
    );
  }

  openMinMaxAlert(message, isMinLimit) {
    const amount = this.findAmountValue(message);
        
    this.routeProvider.openActionSheet(
      this.routeProvider.pages.ModalAlertPage,
      {
        image: 'assets/imgs/yellow-warning_bg_color.svg',
        title: isMinLimit ? this.translate.instant('page.recipientBankPage.minAmountTitle') : this.translate.instant('page.recipientBankPage.maxAmountTitle'),
        subtitle: isMinLimit ? this.translate.instant('page.recipientBankPage.minAmountText', { amount: amount, currency: getCurrencyDisplayName(this.currencyCode) }) : this.translate.instant('page.recipientBankPage.maxAmountText', { amount: amount, currency: getCurrencyDisplayName(this.currencyCode) }),
        primaryButtonText: this.translate.instant('page.identityVerificationPage.identityVerificationChangeAmountButton'),
        showPrimaryButton: true,
        primaryAction: () => this.redirectToCheckPrices(),
        showSecondaryButton: true,
        secondaryButtonText: this.translate.instant('page.recipientBankPage.changeBankButton'),
        secondaryAction: () => this.dismissMinMaxAlert(),
        showFullPage: false,
        isModal: true,
        hideHeader: true,
      }, null
    );
  }

  /** The bank cannot receive the transfer under any account format, so the only way on is another bank. */
  openUnsupportedAccountFormatAlert() {
    this.routeProvider.openActionSheet(
      this.routeProvider.pages.ModalAlertPage,
      {
        image: 'assets/imgs/yellow-warning_bg_color.svg',
        title: this.translate.instant('page.recipientBankPage.formatNotSupported.title'),
        subtitle: this.translate.instant('page.recipientBankPage.formatNotSupported.message'),
        primaryButtonText: this.translate.instant('page.recipientBankPage.formatNotSupported.button'),
        showPrimaryButton: true,
        primaryAction: () => this.openBankSearchScreen(),
        showSecondaryButton: false,
        showFullPage: false,
        isModal: true,
        hideHeader: true,
      }, null
    );
  }

  /** Leaves no unusable fields on screen behind the alert. */
  private clearBankFieldsForUnsupportedFormat() {
    this.loadingFields = false;
    this.hasBank = false;
    this.fields = null;
    this.onSelectedBank.emit(null);
  }

  openCannotRetreiveBankFieldsAlert() {
    this.routeProvider.openActionSheet(
      this.routeProvider.pages.ModalAlertPage,
      {
        image: 'assets/imgs/yellow-warning_bg_color.svg',
        title: this.translate.instant('page.recipientBankPage.bankNotSupported.title'),
        subtitle: this.translate.instant('page.recipientBankPage.bankNotSupported.message'),
        primaryButtonText: this.translate.instant('page.recipientBankPage.bankNotSupported.button'),
        showPrimaryButton: true,
        primaryAction: () => this.openBankSearchScreen(),
        showSecondaryButton: false,
        showFullPage: false,
        isModal: true,
        hideHeader: true,
      }, null
    );
  }

  // function to extract the amount value (in decimal) from errorMessage
  findAmountValue(message) {
    const regex = /\d+\.\d+/;
    const match = message.match(regex);
    return match ? match[0] : null;    
  }

  dismissMinMaxAlert() {
    this.hasBank = false;
  }

  redirectToCheckPrices() {
    const params = {
      bankId: this.bankId,
      amountFrom: this.amountFrom,
      deliveryMethodID: 2, 
      countryTo: this.country,
      currencyTo: this.currencyCode,
      goToActivity: true
    }
    this.routeProvider.push(this.routeProvider.pages.CheckPricesPage, params, false);
  }

  openBankSearchScreen = async () => {
    if (this.openingBankSearch)
      return;
    this.openingBankSearch = true;
    try {
      await this.ensureSmartRoutingCorridor();
      this.pushBankSearchScreen();
    }
    catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.ComponentException, 'Failed to open the bank search screen from the bank deposit component');
    }
    finally {
      this.openingBankSearch = false;
    }
  }

  private pushBankSearchScreen(): void {
    if (this.topCountriesList.includes(this.country)) {
        const params = {
          country: this.country,
          currency: this.currencyCode,
          amount: this.amountFrom,
          recipient: this.recipient,
          bankId: this.recipient ? this.recipient.bankId : "",
          preSelectedBank: this.preSelectedBank,
          order: this.order,
          smartRoutingCorridor: this.smartRoutingCorridor
        }
        this.openTopBanksSearchPage(params);
    }
    else {
      this.routeProvider.openModal(this.routeProvider.pages.SendToBankSearchPage, {
        recipient: this.recipient,
        country: this.country,
        currencyCode: this.currencyCode,
        amountFrom: this.amountFrom,
        smartRoutingCorridor: this.smartRoutingCorridor,
        headerTitle: this.isShowBankNamePlaceHolder ? 'form.bankSearchForm.bankSearchBy.label' : null,
      },(data) => {
        if (data) {
          let bank = data.bank;
          let routingCode = data.routingCode;
          if (bank) {
            this.selectBank(bank, routingCode);
          }
        }
      });
    }
  }

  openTopBanksSearchPage(params){
    this.routeProvider.openModal(this.routeProvider.pages.TopBanksPage, params,
      (data) => {
        if (data) {
          let bank = data.bank;
          this.preSelectedBank = data.bank;
          let routingCode = data.routingCode;
          if (bank) {
            this.selectBank(bank, routingCode);
          }
        }
      }
    );
  }

  async save(recipient: IRecipient) {
    if (!this.hasBank || !this.selectedBank)
      return null;

    let payload = this.form.getPayload();

    if (this.activeMergeConfig) {
      payload = FormFieldMergeTransformer.transformPayload(payload, this.activeMergeConfig);
    }

    payload = { ...recipient, ...payload, bankId: this.bankId, bankAccountFormatId: this.bankAccountFormatId };

    const smartRoutingProvider = this.resolvedProviderForSave();
    if (smartRoutingProvider) {
      payload = {
        ...payload,
        isSmartRouting: true,
        bankProviderId: smartRoutingProvider.providerId,
        bankAgentId: smartRoutingProvider.correspondentId,
        bankAgentLocationId: smartRoutingProvider.correspondentLocId
      };
    }

    // only update bank name when bank has been changed since the bank name sometimes differ from the one saved in the recipient
    // hence it would lead to an unnecessary bank update (orig !== save) which would fail because of the masked fields
    // (i.e. bankId 5900101 returns in the search the bank name SANTANDER/SERFIN but in recipient is BANCO SANTANDER (MEXICO), S.A.)
    if (payload.bankId !== recipient.bankId)
      payload = { ...payload, bankName: this.selectedBank.bankName };

    try {
      await this.validateAccountInRealTime(payload, smartRoutingProvider);
    } catch (ex) {
      // Only a rejection blocks the save; anything unexpected fails open.
      if (ex && ex.realTimeValidation)
        throw ex;
      LoggerProvider.exception(this, ex, LoggerEvent.ComponentException, 'Real-time bank account validation failed, continuing without it');
    }

    const saveResult = await this.recipientProvider.saveRecipientBankAccount(payload, this.currencyCode, this.amountFrom);
    const vop = saveResult && saveResult.vop ? saveResult.vop : null;
    payload = { ...payload, bankAdded: true, vop };
    if (payload.bankAccountFormatId == BankAccountFormatId.PagoMovil && payload.virtualPaymentAccountNo)
      this.order.isPagoMovil = true;
    else
      this.order.isPagoMovil = false;
    const order = this.orderProvider.getCurrentOrder();
    this.order = {...this.order, ...order};

    this.orderProvider.setCurrentOrder(this.order);
    return payload;
  }

  private resolvedProviderForSave(): IBankDepositSmartRoutingProvider | null {
    if (this.isWalletBankTransfer)
      return null;

    const order = this.orderProvider.getCurrentOrder() || {} as IOrder;
    const deliveryMethod = this.order.deliveryMethodInternal || order.deliveryMethodInternal;
    if (deliveryMethod !== DeliveryMethodType.BankDeposit)
      return null;

    if (!this.bankDepositSmartRoutingProvider.isSmartRoutedTransaction)
      return null;

    const provider = this.bankDepositSmartRoutingProvider.bankDepositProvider;
    if (Number(provider.bankId) !== Number(this.bankId)) {
      LoggerProvider.error(this, LoggerEvent.ComponentException,
        `Smart routing resolved provider for bank ${provider.bankId} but the save is for bank ${this.bankId}, saving on legacy routing`);
      return null;
    }

    return provider;
  }

  private async validateAccountInRealTime(payload: any, smartRoutingProvider: IBankDepositSmartRoutingProvider | null) {
    if (!smartRoutingProvider)
      return;

    const rejection = await this.bankDepositSmartRoutingProvider.findAccountRejection({
      countryTo: this.country,
      bankId: this.bankId,
      accountNumber: payload.bankAccountNumber,
      unitaryAccountNumber: payload.bankAccountNumberUnitary,
      currency: this.currencyCode,
      beneficiaryId: payload.beneficiaryId
    });
    if (!rejection)
      return;

    LoggerProvider.warning(this, LoggerEvent.ComponentException, 'Real-time validation rejected the bank account, blocking the save');
    throw {
      errorCode: ErrorCodeType.RecipientBankDepositDetailsInvalid,
      errorMessage: BankDepositSmartRoutingProvider.rejectionMessage(rejection),
      realTimeValidation: rejection
    };
  }

  showAddFromContacts(): boolean {
    return !!(this.activeMergeConfig && this.activeMergeConfig.prefixFieldId);
  }

  async addFromContacts() {
    try {
      const contact = await this.contactsProvider.pickContact(this.contactsProvider.reasons.addMobileWallet);
      if (!contact || !contact.phoneNumbers || !contact.phoneNumbers.length || !this.activeMergeConfig)
        return;

      const mobile = contact.phoneNumbers.find(number => number.type === 'mobile') || contact.phoneNumbers[0];
      if (!mobile || !mobile.value)
        return;

      const form = this.getForm();
      const control = form && form.controls[this.activeMergeConfig.valueFieldId];
      if (!control)
        return;

      control.setValue(this.activeMergeConfig.normalize(mobile.value));
      control.markAsTouched();
    } catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.ComponentException, 'Failed to pick a contact for the Pago Movil phone field');
    }
  }

  presentBranchLookup() {
    this.routeProvider.openModal(this.routeProvider.pages.BranchLookupPage, {countryTo: this.country, bankId: this.selectedBankId, isBankRoutingCodeEnabled: this.bankRoutingCodeFieldExists, showBackdrop: false}, (selectedBranch) => {
      if(selectedBranch != null) {
        if(this.bankRoutingCodeFieldExists) {
          this.form.form.controls.bankRoutingCode.setValue(selectedBranch.bankLocationRoutingCode)
        } else if(this.bankLocationNumberFieldExists && this.bankLocationNameFieldExists) {
          this.form.form.controls.bankLocationName.setValue(selectedBranch.bankLocationName)
          this.form.form.controls.bankLocationNumber.setValue(selectedBranch.bankLocationNumber)
        } else if(this.bankLocationNumberFieldExists) {
          this.form.form.controls.bankLocationNumber.setValue(selectedBranch.bankLocationNumber)
        } else if(this.bankLocationNameFieldExists) {
          this.form.form.controls.bankLocationName.setValue(selectedBranch.bankLocationName)
        } else {
          this.form.form.controls.bankLocationNumber.setValue("")
        }
      }
    }, 'branch-lookup-modal');
  }

  getFontSize(element) {
    if (element && element.value) {
      if (element.value.length >= 45)
        return '12px';
      else if (element.value.length >= 40)
        return '13px';
      else if (element.value.length >= 35)
        return '14px';
      else if (element.value.length >= 30)
        return '15px';
    }
    return '16px';
  }

  checkIfBankRoutingCodeExist(fields: IFormControl[]) {
    fields.forEach(field => {
      if(field.id == 'bankRoutingCode') {
        this.bankRoutingCodeFieldExists = true
      }
    })
  }

  checkIfBankLocationNumberExist(fields: IFormControl[]) {
    fields.forEach(field => {
      if(field.id == 'bankLocationNumber') {
        this.bankLocationNumberFieldExists = true
      }
    })
  }

  checkIfBankLocationNameFieldExist(fields: IFormControl[]) {
    fields.forEach(field => {
      if(field.id == 'bankLocationName') {
        this.bankLocationNameFieldExists = true
      }
    })
  }

  isWarningCountry() {
      return this.country === "CO";
  }

  async onWarningAlertClick() {
    const linkWarn = this.translate.instant("page.sendMoneyRecipientBankDepositPage.learnMoreAlertLink");
    const linkURL = await this.helpProvider.createHelpLink(linkWarn);
    this.routeProvider.openIab(linkURL);
  }

  /**
   * Resolves the corridor gate before anything reads it; cached per corridor, so repeats cost nothing.
   * The entry points outside the send flow (a recipient's profile, the bank details update after a failed
   * validate) are handed no order, so the send currency falls back to the current order and then to the
   * region's own — without it the gate reads as incomplete and those screens stay on the legacy engine.
   */
  private async ensureSmartRoutingCorridor(): Promise<void> {
    // A wallet bank transfer is not a bank deposit payout and never routes a provider, so it resolves only
    // what its own screen carries. Every other entry point gets the fallbacks below.
    const fallback: any = this.isWalletBankTransfer ? {} : (this.orderProvider.getCurrentOrder() || {});
    const defaultCurrencyFrom = this.isWalletBankTransfer ? null : APP_CONFIG.region.defaultCurrencyFrom;
    this.smartRoutingCorridor = {
      countryTo: this.country,
      currencyFrom: this.order.currencyFrom || fallback.currencyFrom || defaultCurrencyFrom,
      currencyTo: this.currencyCode,
      amountFrom: this.amountFrom,
      amountTo: this.order.amountTo || fallback.amountTo
    };
    try {
      await this.bankDepositSmartRoutingProvider.ensureSmartRoutingEnabled(this.smartRoutingCorridor);
    }
    catch (ex) {
      // The bank screens have to work either way; an unresolved gate only leaves them on the legacy engine.
      LoggerProvider.exception(this, ex, LoggerEvent.ComponentException, 'Failed to resolve the bank deposit smart routing gate, continuing on the legacy engine');
    }
  }

  /** Bank-details form from transactionRequirements. Null on failure, so the caller falls back to legacy. */
  private async getSmartRoutingBankFields(params: any): Promise<IFormControl[]> {
    try {
      const res = await this.bankDepositSmartRoutingProvider.getTransactionRequirements({
        countryTo: this.country,
        currencyTo: this.currencyCode,
        currencyFrom: this.order.currencyFrom,
        bankId: this.bankId,
        deliveryMethodId: params.deliveryMethodID,
        amountFrom: this.amountFrom,
        amountTo: this.order.amountTo,
        bankAccountFormatId: params.bankAccountFormatId ? Number(params.bankAccountFormatId) : undefined,
        recipientId: this.recipient ? this.recipient.id : undefined
      });

      // Ahead of the empty-form fallback below: a bank with no usable format carries no fields either,
      // and falling back would put the customer on a legacy form the transfer can never be paid out from.
      if (res && res.IsFormatSupported === false) {
        this.bankDepositSmartRoutingProvider.clearResolvedProvider();
        this.unsupportedAccountFormat = true;
        LoggerProvider.info(this, `Bank ${this.bankId} supports no account format for this transfer, asking the customer for another bank`);
        return null;
      }

      const fields = FormControlMapper.fromTransactionRequirements(res);
      if (!fields || fields.length === 0) {
        // The response already recorded a provider. Dropping it keeps the account data the legacy form
        // is about to collect from being paid out through a correspondent smart routing picked.
        this.bankDepositSmartRoutingProvider.clearResolvedProvider();
        LoggerProvider.error(this, LoggerEvent.ComponentException, 'Bank deposit transaction requirements carried no form fields, falling back to the legacy bank form');
        return null;
      }

      this.applyBankAccountFormats(
        (res.AccountFormats) ? res.AccountFormats.map((format) => ({ value: format.Id.toString(), text: format.Label })) : null,
        res.AccountFormatLabel);
      return fields;
    }
    catch (ex) {
      // A failed call clears the provider on its own, but a mapping failure after a recorded response does not.
      this.bankDepositSmartRoutingProvider.clearResolvedProvider();
      LoggerProvider.exception(this, ex, LoggerEvent.ComponentException, 'Bank deposit transaction requirements failed, falling back to the legacy bank form');
      return null;
    }
  }

  /** The account-format picker, fed from either endpoint. May correct bankAccountFormatId, which getBankFields detects and re-fetches on. */
  private applyBankAccountFormats(formats: { value: string, text: string }[], title: string) {
    this.bankAccountFormats = [];
    if (!formats)
      return;

    this.bankAccountFormatTitle = title;
    const hasPagoMovil = formats.some((format) => format.value == BankAccountFormatId.PagoMovil.toString());
    this.showPagoMovilFormatField = hasPagoMovil;
    this.hideAccountFormatField = hasPagoMovil && this.country !== 'VE';
    this.bankAccountFormats = formats;

    // If the current format is not supported by this bank, reset to the bank's default
    const formatIsValid = this.bankAccountFormatId && this.bankAccountFormats.find(format => format.value == this.bankAccountFormatId);
    if (!formatIsValid && this.bankAccountFormats.length > 0) {
      this.bankAccountFormatId = this.bankAccountFormats[0].value;
    }

    if (this.format && this.bankAccountFormats.length > 0) {
      this.format.setValue(this.bankAccountFormatId, false, this.bankAccountFormats);
    }
  }

  changeBankAccountFormat(item) {
    const formatId = item ? item.value : null;
    // Only a different format makes the recipient's account details stale; re-picking the same one keeps them.
    if (formatId != this.bankAccountFormatId)
      this.preserveAccountDetails = false;
    this.bankAccountFormatId = formatId;

    this.getBankFields();
  }

  /**
   * Prefills the form from the recipient. Across a format change (send again reselection only) the
   * format-specific fields carry over only between the account number and unitary formats, since a
   * CLABE, IBAN or IFSC is not valid under another format. The number moves into whichever of the
   * two account fields the new format asks for.
   */
  private prefillRecipientFields(fields: IFormControl[], sameFormat: boolean) {
    const sameAccountNumber = INTERCHANGEABLE_ACCOUNT_FORMATS.indexOf(String(this.recipient.bankAccountFormatId)) !== -1
      && INTERCHANGEABLE_ACCOUNT_FORMATS.indexOf(String(this.bankAccountFormatId)) !== -1;
    const accountNumber = this.recipient.bankAccountNumberUnitary || this.recipient.bankAccountNumber;
    for (let i = 0; i < fields.length; i++) {
      const field = fields[i];
      let value = this.recipient[field.id];
      if (!sameFormat && FORMAT_SPECIFIC_FIELDS.indexOf(field.id) !== -1) {
        if (!sameAccountNumber)
          continue;
        if (field.id === 'bankAccountNumber' || field.id === 'bankAccountNumberUnitary')
          value = accountNumber;
      }
      if (value) {
        field.value = value;
      }
    }
  }
}



// WEBPACK FOOTER //
// ./src/components/rmt-bank-deposit/rmt-bank-deposit.ts